import type {Values} from '@wix/devzai-utils-common';
import {evaluateFunction} from '@wix/devzai-utils-common';
import type {DoppeActionType} from '../doppe-action-types/doppe-action-type';
import {
    doppeActionTypeTestimonialsList
} from '../../doppe-action-types/doppe-action-type-testimonials-list/doppe-action-type-testimonials-list';
import {
    doppeActionTypeMediaGallery
} from '../../doppe-action-types/doppe-action-type-media-gallery/doppe-action-type-media-gallery';
import {
    doppeActionTypeEmbedPageList
} from '../../doppe-action-types/doppe-action-type-embed-page-list/doppe-action-type-embed-page-list';
import {DoppeListActionItemLayout} from './doppe-list-action-settings';

export const DoppeActionLayout = {
    Column: 'Column',
    Grid: 'Grid',
    Carousel: 'Carousel',
    CarouselSmall: 'CarouselSmall',
    Swiper: 'Swiper',
    ImagesGrid: 'ImagesGrid',
    LargeColumn: 'LargeColumn',
} as const;

export type DoppeActionLayout = Values<typeof DoppeActionLayout>;

export const DoppeActionLayoutMediaRatio = {
    Ratio21By9: 'Ratio21By9',
    Ratio2By1: 'Ratio2By1',
    Ratio16By9: 'Ratio16By9',
    Ratio3By2: 'Ratio3By2',
    Ratio4By3: 'Ratio4By3',
    Ratio5By4: 'Ratio5By4',
    Square: 'Square',
    SquarePortrait: 'SquarePortrait',
    Ratio9By21: 'Ratio9By21',
    Ratio1By2: 'Ratio1By2',
    Ratio9By16: 'Ratio9By16',
    Ratio2By3: 'Ratio2By3',
    Ratio3By4: 'Ratio3By4',
    Ratio4By5: 'Ratio4By5',
} as const;

export type DoppeActionLayoutMediaRatio = Values<typeof DoppeActionLayoutMediaRatio>;

export const DoppeActionLayoutMediaSize = {
    Small: 'Small',
    Large: 'Large',
} as const;

export type DoppeActionLayoutMediaSize = Values<typeof DoppeActionLayoutMediaSize>;


export function doppeActionLayoutGetRatios(isPortrait: boolean) {
    return isPortrait ?
        [
            DoppeActionLayoutMediaRatio.SquarePortrait,
            DoppeActionLayoutMediaRatio.Ratio4By5,
            DoppeActionLayoutMediaRatio.Ratio3By4,
            DoppeActionLayoutMediaRatio.Ratio2By3,
            DoppeActionLayoutMediaRatio.Ratio9By16,
            DoppeActionLayoutMediaRatio.Ratio1By2,
            DoppeActionLayoutMediaRatio.Ratio9By21,
        ] :
        [
            DoppeActionLayoutMediaRatio.Square,
            DoppeActionLayoutMediaRatio.Ratio5By4,
            DoppeActionLayoutMediaRatio.Ratio4By3,
            DoppeActionLayoutMediaRatio.Ratio3By2,
            DoppeActionLayoutMediaRatio.Ratio16By9,
            DoppeActionLayoutMediaRatio.Ratio2By1,
            DoppeActionLayoutMediaRatio.Ratio21By9,
        ];
}

export function doppeActionLayoutIsRatioPortrait (ratio: DoppeActionLayoutMediaRatio) {
    return evaluateFunction(() => {
        switch (ratio) {
            case DoppeActionLayoutMediaRatio.SquarePortrait:
            case DoppeActionLayoutMediaRatio.Ratio9By21:
            case DoppeActionLayoutMediaRatio.Ratio1By2:
            case DoppeActionLayoutMediaRatio.Ratio9By16:
            case DoppeActionLayoutMediaRatio.Ratio2By3:
            case DoppeActionLayoutMediaRatio.Ratio3By4:
            case DoppeActionLayoutMediaRatio.Ratio4By5: {
                return true;
            }
            default: {
                return false;
            }
        }
    });
}

export function doppeActionLayoutGetDefaultMediaSizeForLayout(layout: DoppeActionLayout) {
    switch (layout) {
        case DoppeActionLayout.ImagesGrid: {
            return DoppeActionLayoutMediaSize.Small;
        }
        case DoppeActionLayout.Carousel: {
            return DoppeActionLayoutMediaSize.Large;
        }
        case DoppeActionLayout.Column:
        case DoppeActionLayout.CarouselSmall: {
            return DoppeActionLayoutMediaSize.Small;
        }
        case DoppeActionLayout.Grid:
        case DoppeActionLayout.LargeColumn:
        case DoppeActionLayout.Swiper: {
            return null;
        }
    }
}

export function doppeActionLayoutGetDefaultItemLayout(layout: DoppeActionLayout, actionType: DoppeActionType<any> ) {
    switch (layout) {
        case DoppeActionLayout.Grid:
        case DoppeActionLayout.ImagesGrid:
        case DoppeActionLayout.Column:
        case DoppeActionLayout.CarouselSmall: {
            return DoppeListActionItemLayout.Default;
        }
        case DoppeActionLayout.LargeColumn:
        case DoppeActionLayout.Swiper:
        case DoppeActionLayout.Carousel: {
            return actionType.id === doppeActionTypeTestimonialsList.id ? DoppeListActionItemLayout.FullDetailsWithShowMore : DoppeListActionItemLayout.Default;
        }
    }
}




export function doppeActionLayoutGetDefaultRatioForLayout(layout: DoppeActionLayout) {
    switch (layout) {
        case DoppeActionLayout.Column: {
            return DoppeActionLayoutMediaRatio.Square;
        }
        case DoppeActionLayout.Grid: {
            return DoppeActionLayoutMediaRatio.Ratio5By4;
        }
        case DoppeActionLayout.ImagesGrid: {
            return DoppeActionLayoutMediaRatio.Square;
        }
        case DoppeActionLayout.LargeColumn: {
            return DoppeActionLayoutMediaRatio.Ratio3By2;
        }
        case DoppeActionLayout.Swiper: {
            return DoppeActionLayoutMediaRatio.Ratio5By4;
        }
        case DoppeActionLayout.Carousel: {
            return DoppeActionLayoutMediaRatio.Ratio5By4;
        }
        case DoppeActionLayout.CarouselSmall: {
            return DoppeActionLayoutMediaRatio.Ratio5By4;
        }
    }
}

export function doppeActionLayoutMediaRatioResolveOppositeRatio (ratio: DoppeActionLayoutMediaRatio) {
    switch (ratio) {
        case DoppeActionLayoutMediaRatio.SquarePortrait: return DoppeActionLayoutMediaRatio.Square;
        case DoppeActionLayoutMediaRatio.Square: return DoppeActionLayoutMediaRatio.SquarePortrait;
        case DoppeActionLayoutMediaRatio.Ratio5By4: return DoppeActionLayoutMediaRatio.Ratio4By5;
        case DoppeActionLayoutMediaRatio.Ratio4By3: return DoppeActionLayoutMediaRatio.Ratio3By4;
        case DoppeActionLayoutMediaRatio.Ratio3By2: return DoppeActionLayoutMediaRatio.Ratio2By3;
        case DoppeActionLayoutMediaRatio.Ratio16By9: return DoppeActionLayoutMediaRatio.Ratio9By16;
        case DoppeActionLayoutMediaRatio.Ratio21By9: return DoppeActionLayoutMediaRatio.Ratio9By21;
        case DoppeActionLayoutMediaRatio.Ratio2By1: return DoppeActionLayoutMediaRatio.Ratio1By2;
        case DoppeActionLayoutMediaRatio.Ratio9By16: return DoppeActionLayoutMediaRatio.Ratio16By9;
        case DoppeActionLayoutMediaRatio.Ratio1By2: return DoppeActionLayoutMediaRatio.Ratio2By1;
        case DoppeActionLayoutMediaRatio.Ratio2By3: return DoppeActionLayoutMediaRatio.Ratio3By2;
        case DoppeActionLayoutMediaRatio.Ratio3By4: return DoppeActionLayoutMediaRatio.Ratio4By3;
        case DoppeActionLayoutMediaRatio.Ratio4By5: return DoppeActionLayoutMediaRatio.Ratio5By4;
        case DoppeActionLayoutMediaRatio.Ratio9By21: return DoppeActionLayoutMediaRatio.Ratio21By9;
    }
}

export function doppeActionLayoutMediaRatioResolveWidthHeightRatio (ratio: DoppeActionLayoutMediaRatio) {

    const calculatedRatio = evaluateFunction(() => {
        switch (ratio) {
            case DoppeActionLayoutMediaRatio.SquarePortrait: return 1;
            case DoppeActionLayoutMediaRatio.Square: return 1;
            case DoppeActionLayoutMediaRatio.Ratio5By4: return 5 / 4;
            case DoppeActionLayoutMediaRatio.Ratio4By3: return 4 / 3;
            case DoppeActionLayoutMediaRatio.Ratio3By2: return 3 / 2;
            case DoppeActionLayoutMediaRatio.Ratio16By9: return 16 / 9;
            case DoppeActionLayoutMediaRatio.Ratio21By9: return 21 / 9;
            case DoppeActionLayoutMediaRatio.Ratio2By1: return 2;
            case DoppeActionLayoutMediaRatio.Ratio9By16: return 9 / 16;
            case DoppeActionLayoutMediaRatio.Ratio1By2: return 1 / 2;
            case DoppeActionLayoutMediaRatio.Ratio2By3: return 2 / 3;
            case DoppeActionLayoutMediaRatio.Ratio3By4: return 3 / 4;
            case DoppeActionLayoutMediaRatio.Ratio4By5: return 4 / 5;
            case DoppeActionLayoutMediaRatio.Ratio9By21: return 9 / 21;
        }
    });

    return calculatedRatio;
}

export function doppeActionLayoutCanHideImage (doppeActionLayout: DoppeActionLayout) {
    switch (doppeActionLayout) {
        case DoppeActionLayout.Column: {
            return true;
        }
        default: {
            return false;
        }
    }
}

export function doppeActionLayoutCanHideDescription (doppeActionLayout: DoppeActionLayout) {
    switch (doppeActionLayout) {
        case DoppeActionLayout.LargeColumn:
        case DoppeActionLayout.Column: {
            return true;
        }
        default: {
            return false;
        }
    }
}

export function doppeActionLayoutSupportsDescription (doppeActionLayout: DoppeActionLayout) {
    switch (doppeActionLayout) {
        case DoppeActionLayout.LargeColumn:
        case DoppeActionLayout.Column: {
            return true;
        }
        default: {
            return false;
        }
    }
}

export function doppeActionLayoutCanHideTitle (doppeActionLayout: DoppeActionLayout) {
    switch (doppeActionLayout) {
        case DoppeActionLayout.LargeColumn:
        case DoppeActionLayout.Swiper:
        case DoppeActionLayout.CarouselSmall:
        case DoppeActionLayout.Carousel:
        case DoppeActionLayout.Grid: {
            return true;
        }
        default: {
            return false;
        }
    }
}

export function doppeActionLayoutSupportDisplayListItemSettings (_doppeActionLayout: DoppeActionLayout, itemLayout: DoppeListActionItemLayout) {
    return itemLayout === DoppeListActionItemLayout.Default;
}

export function doppeActionLayoutSupportsImageRatio (doppeActionLayout: DoppeActionLayout, itemLayout: DoppeListActionItemLayout) {
    switch (doppeActionLayout) {
        case DoppeActionLayout.LargeColumn:
        case DoppeActionLayout.Swiper:
        case DoppeActionLayout.Carousel: {
            return itemLayout !== DoppeListActionItemLayout.FullDetailsWithShowMore;
        }
        case DoppeActionLayout.Column:
        case DoppeActionLayout.ImagesGrid:
        case DoppeActionLayout.CarouselSmall:
        case DoppeActionLayout.Grid: {
            return true;
        }
        default: {
            return false;
        }
    }
}

export function doppeActionLayoutSupportsMediaSize (doppeActionLayout: DoppeActionLayout, itemLayout: DoppeListActionItemLayout) {

    switch (doppeActionLayout) {
        case DoppeActionLayout.Carousel: {
            return itemLayout !== DoppeListActionItemLayout.FullDetailsWithShowMore;
        }
        case DoppeActionLayout.CarouselSmall: {
            return true;
        }
        case DoppeActionLayout.Column:
        case DoppeActionLayout.ImagesGrid: {
            return true;
        }
        case DoppeActionLayout.LargeColumn:
        case DoppeActionLayout.Grid:
        case DoppeActionLayout.Swiper: {
            return false;
        }
        default: {
            return false;
        }
    }
}


/**
 * Checks if the layout supports more than one item layout option.
 * Returns true only if there are at least 2 options available (more than just Default).
 */
export function doppeActionLayoutSupportsItemLayout(
    doppeActionLayout: DoppeActionLayout,
    actionType: DoppeActionType<any>,
    options?: { withFullDetails?: boolean }
) {
    const supportsShowMore = doppeActionLayoutSupportsItemLayoutShowMore(doppeActionLayout, actionType);
    const supportsFullDetails = options?.withFullDetails && doppeActionLayoutSupportsItemLayoutFullDetails(doppeActionLayout, actionType);
    
    // Return true if there's more than just the Default option
    return supportsShowMore || supportsFullDetails;
}

export function doppeActionLayoutSupportsItemLayoutShowMore (doppeActionLayout: DoppeActionLayout, actionType: DoppeActionType<any>) {
    switch (doppeActionLayout) {
        case DoppeActionLayout.LargeColumn:
        case DoppeActionLayout.Swiper:
        case DoppeActionLayout.Carousel: {
            return actionType.id === doppeActionTypeTestimonialsList.id;
        }
        case DoppeActionLayout.CarouselSmall:
        case DoppeActionLayout.Grid:
        case DoppeActionLayout.Column:
        case DoppeActionLayout.ImagesGrid: {
            return false;
        }
        default: {
            return false;
        }
    }
}


export function doppeActionLayoutSupportsItemLayoutFullDetails (doppeActionLayout: DoppeActionLayout, actionType: DoppeActionType<any>) {
    // Gallery and embed list action types do not support full details
    if (actionType.id === doppeActionTypeMediaGallery.id || actionType.id === doppeActionTypeEmbedPageList.id) {
        return false;
    }

    switch (doppeActionLayout) {
        case DoppeActionLayout.LargeColumn: {
            return true;
        }
        case DoppeActionLayout.Swiper:
        case DoppeActionLayout.Carousel:
        case DoppeActionLayout.CarouselSmall:
        case DoppeActionLayout.Grid:
        case DoppeActionLayout.Column:
        case DoppeActionLayout.ImagesGrid: {
            return false;
        }
        default: {
            return false;
        }
    }
}



export function doppeActionLayoutCanHidePrice (doppeActionLayout: DoppeActionLayout) {
    switch (doppeActionLayout) {
        case DoppeActionLayout.Column: {
            return false;
        }
        default: {
            return true;
        }
    }
}

export function doppeActionLayoutCanHideTextDecorationsOnImage (_doppeActionLayout: DoppeActionLayout) {
    return true;
}




